/**
 * Screen 02 — IFA · Verify SEBI registration.
 *
 * Shown only to first-time IFAs after Google sign-in, before approval.
 * Captures the 9-digit INA suffix + two consent checkboxes. Submit
 * writes the fields onto the user profile and routes to ApprovalPending.
 *
 * Status stays "pending" — admin flips it via the manual gmail-approval
 * flow (Firebase Console or a Firestore Function with a magic-link
 * approve button — out of scope for this commit).
 *
 * Per locked behavioural change: copy reads "Submit for review" — no
 * "instant" / "manual" / timeline language.
 */
import { useState, type FormEvent } from "react";
import { useNavigate } from "react-router-dom";
import { doc, updateDoc } from "firebase/firestore";
import { AppNav } from "@/components/AppNav";
import { IconArrowRight, IconCheck, IconWA } from "@/components/icons";
import { useAuth } from "@/auth";
import { db } from "@/firebase";
import { Routes } from "@/lib/routes";
import { notifyAdviserRegistration } from "@/lib/api";

export function IFAVerify() {
  const nav = useNavigate();
  const { user, profile } = useAuth();
  // MFD attests an AMFI (ARN) number; IFA/RIA attests a SEBI (INA…) number.
  // Both land in the same `sebiRegistration` field the entitlement gate reads.
  const isMFD = profile?.role === "mfd";
  const isRA = profile?.role === "ra";
  const [researchRegistrationType, setResearchRegistrationType] = useState<"INH" | "INA">("INH");
  // NEVER pre-fill this. It used to seed "100024187" — the owner's own
  // registration (see .env.yaml) — into every new IFA's form. Nine digits
  // passes `regValid`, so a stranger who simply ticked the two attestations
  // submitted the owner's SEBI number as their own, onto the very field
  // `core.deps.research_entitled` reads to unlock research generation.
  const [ina, setIna] = useState("");
  const [acceptedActive, setAcceptedActive] = useState(false);
  const [acceptedAid, setAcceptedAid] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const initials = (profile?.displayName ?? user?.email ?? "U")
    .split(" ")
    .map((p) => p[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();
  const firstName = (profile?.displayName ?? "there").split(" ")[0];

  const regValid = isMFD ? /^\d{3,7}$/.test(ina.trim()) : /^\d{9}$/.test(ina.trim());
  const canSubmit = !!user && regValid && acceptedActive && acceptedAid && !submitting;

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!user || !canSubmit) return;
    setSubmitting(true);
    setError(null);
    try {
      await updateDoc(doc(db, "pro_users", user.uid), {
        sebiRegistration: isMFD ? `ARN-${ina.trim()}` : `${isRA ? researchRegistrationType : "INA"}${ina.trim()}`,
        acceptedActiveSebi: true,
        acceptedResearchAid: true,
      });
      // Tell the owner someone is waiting. Fire-and-forget: the registration is
      // already saved, and a failed notice is caught by the daily digest.
      notifyAdviserRegistration().catch(() => {});
      nav(Routes.Pending, { replace: true });
    } catch (err) {
      console.error("[ifa-verify] submit failed:", err);
      setError("Couldn't save your registration. Try again in a moment.");
      setSubmitting(false);
    }
  };

  return (
    <div className="col" style={{ minHeight: "100%", background: "var(--paper-2)" }}>
      <AppNav mode="signup" showSignOut />

      <main
        style={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          padding: "40px 48px 56px",
        }}
      >
        <div style={{ maxWidth: 920, width: "100%" }}>
          <div className="row gap-4 center" style={{ marginBottom: 28 }}>
            <div
              className="av lg"
              style={{
                width: 52,
                height: 52,
                borderRadius: 12,
                fontSize: 19,
                background: "var(--brand-soft)",
                color: "var(--brand-deep)",
              }}
            >
              {initials}
            </div>
            <div>
              <div className="kicker">
                <span className="sq" />
                Account setup · last step
              </div>
              <h1 className="h-display" style={{ fontSize: 32, marginTop: 6 }}>
                One more thing, {firstName}.
              </h1>
              <p
                className="muted"
                style={{
                  fontSize: 14.5,
                  marginTop: 6,
                  lineHeight: 1.55,
                  maxWidth: "60ch",
                }}
              >
                {isMFD
                  ? "Confirm your AMFI (ARN) registration. We'll cross-check it against the AMFI registry as part of activating your workspace."
                  : "Confirm your SEBI registration number. We'll cross-check it against the SEBI public registry as part of activating your workspace."}
              </p>
            </div>
          </div>

          <div
            style={{ display: "grid", gridTemplateColumns: "1.5fr 1fr", gap: 18 }}
            className="grid-2"
          >
            <form className="card padded" style={{ padding: 26 }} onSubmit={handleSubmit}>
              <div className="lbl" style={{ marginBottom: 8 }}>
                {isMFD ? "AMFI registration (ARN)" : "SEBI registration number"}
              </div>
              {isRA && <label className="lbl" style={{ display: "block", marginBottom: 12 }}>
                Registration type
                <select value={researchRegistrationType} onChange={e => setResearchRegistrationType(e.target.value as "INH" | "INA")} style={{ display: "block", marginTop: 6 }}>
                  <option value="INH">Research Analyst (INH)</option>
                  <option value="INA">Investment Adviser (INA)</option>
                </select>
              </label>}
              <div className="verify-input">
                <span className="prefix">{isMFD ? "ARN-" : isRA ? researchRegistrationType : "INA"}</span>
                <input
                  type="text"
                  placeholder={isMFD ? "12345" : "000 000 000"}
                  inputMode="numeric"
                  value={ina}
                  onChange={(e) => setIna(e.target.value.replace(/\D/g, "").slice(0, isMFD ? 7 : 9))}
                />
              </div>
              <p
                className="muted"
                style={{ fontSize: 12, marginTop: 8, lineHeight: 1.5 }}
              >
                {isMFD
                  ? "Format: ARN- followed by your AMFI number. We'll match it against the AMFI registry."
                  : `Format: ${isRA ? researchRegistrationType : "INA"} followed by 9 digits. We'll match this against the active registry.`}
              </p>

              <div
                style={{
                  marginTop: 22,
                  paddingTop: 20,
                  borderTop: "1px solid var(--line-soft)",
                  display: "flex",
                  flexDirection: "column",
                  gap: 14,
                }}
              >
                <label className="check-row">
                  <input
                    type="checkbox"
                    checked={acceptedActive}
                    onChange={(e) => setAcceptedActive(e.target.checked)}
                  />
                  <span className="box">
                    <IconCheck />
                  </span>
                  <span>
                    {isMFD ? (
                      <>I confirm I'm an <strong>active AMFI-registered</strong>{" "}
                      Mutual Fund Distributor and the ARN above is mine.</>
                    ) : (
                      <>I confirm I'm an <strong>active SEBI-registered</strong>{" "}
                      {isRA && researchRegistrationType === "INH" ? "Research Analyst" : "Investment Adviser"} and the registration
                      above is mine.</>
                    )}
                  </span>
                </label>
                <label className="check-row">
                  <input
                    type="checkbox"
                    checked={acceptedAid}
                    onChange={(e) => setAcceptedAid(e.target.checked)}
                  />
                  <span className="box">
                    <IconCheck />
                  </span>
                  <span>
                    I understand StockSense AI outputs are{" "}
                    <strong>research-ideation aids only</strong> and I'll
                    prepare client-facing notes under my own registration.
                  </span>
                </label>
              </div>

              <div className="row gap-2" style={{ marginTop: 22 }}>
                <button
                  className="btn btn-primary btn-lg"
                  style={{ flex: 1, justifyContent: "center" }}
                  type="submit"
                  disabled={!canSubmit}
                >
                  {submitting ? "Submitting…" : "Submit for review"}
                  {!submitting && <IconArrowRight />}
                </button>
              </div>

              {error && (
                <p
                  className="text-loss"
                  style={{ marginTop: 12, fontSize: 13, lineHeight: 1.5 }}
                >
                  {error}
                </p>
              )}

              <p
                style={{
                  marginTop: 14,
                  textAlign: "center",
                  fontSize: 12,
                  color: "var(--ink-light)",
                  lineHeight: 1.55,
                }}
              >
                Don't have a SEBI registration?{" "}
                <a href={Routes.SignUp} style={{ color: "var(--brand)" }}>
                  Continue as Individual Investor instead
                </a>
              </p>
            </form>

            <div className="col gap-3">
              <div className="card padded">
                <div className="lbl" style={{ marginBottom: 12 }}>
                  What we check
                </div>
                <ul
                  style={{
                    listStyle: "none",
                    display: "flex",
                    flexDirection: "column",
                    gap: 9,
                    fontSize: 12.5,
                    color: "var(--ink-mid)",
                    lineHeight: 1.5,
                  }}
                >
                  {[
                    "Number is active on the SEBI public registry",
                    "Registration type covers Research / Advisory",
                    "No public regulatory flags in the last 12 months",
                  ].map((t) => (
                    <li
                      key={t}
                      className="row gap-2"
                      style={{ alignItems: "flex-start" }}
                    >
                      <span
                        style={{
                          width: 14,
                          height: 14,
                          borderRadius: "50%",
                          background: "var(--brand-soft)",
                          color: "var(--brand-deep)",
                          display: "grid",
                          placeItems: "center",
                          flexShrink: 0,
                          marginTop: 2,
                          fontSize: 10,
                        }}
                      >
                        <IconCheck />
                      </span>
                      <span>{t}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="card padded">
                <div className="lbl" style={{ marginBottom: 10 }}>
                  Privacy
                </div>
                <p
                  style={{
                    fontSize: 12.5,
                    color: "var(--ink-mid)",
                    lineHeight: 1.55,
                  }}
                >
                  We use only the SEBI public registry — no private data, no
                  documents requested. Your registration number is stored in your private profile
                  and used to verify access to professional features inside
                  StockSense AI.
                </p>
              </div>

              <div
                className="card padded"
                style={{
                  background: "var(--brand-deep)",
                  color: "#fff",
                  borderColor: "transparent",
                }}
              >
                <div
                  className="lbl"
                  style={{ color: "#9ddc78", marginBottom: 8 }}
                >
                  Stuck on this step?
                </div>
                <p
                  style={{
                    fontSize: 12.5,
                    lineHeight: 1.55,
                    color: "rgba(255,255,255,.82)",
                    marginBottom: 12,
                  }}
                >
                  If the registry lookup fails or your firm uses a different
                  format, drop us a note.
                </p>
                <button
                  className="btn btn-sm"
                  style={{
                    background: "#fff",
                    color: "var(--brand-deep)",
                    padding: "7px 11px",
                  }}
                >
                  <IconWA /> WhatsApp +91 77570 25257
                </button>
              </div>
            </div>
          </div>

          <div
            style={{
              marginTop: 22,
              textAlign: "center",
              fontSize: 11,
              color: "var(--ink-light)",
              fontFamily: "var(--mono)",
              letterSpacing: ".10em",
            }}
          >
            Educational technology tool — not a SEBI-registered RA or IA.
          </div>
        </div>
      </main>
    </div>
  );
}
